import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t["return"] || t["return"](); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
import { ARTIFACT_CACHE_BYTES, MAX_ATTENTION_ITEMS, MAX_EXECUTION_JSON_BYTES, MAX_LOG_TEXT_BYTES } from '../config';
import { BlobByteSource, HttpByteSource } from '../pack/byte-source';
import { parseCoverage } from '../model/coverage';
import { ByteBudgetLru } from '../pack/entry-cache';
import { isAbort, PackError } from '../pack/errors';
import { MeteredByteSource } from '../pack/metered';
import { openPackZip } from '../pack/zip';
import { parseExecution } from '../model/execution';
import { parseHar } from '../model/har';
import { splitNdjsonLines } from '../model/ndjson';
import { listLogs, listSteps, parseFailureDetail, parseFailureIndex, parseRunManifest, parseTestResult, readAttention as _readAttention, toRunVM, toTestRow } from '../model/parse';
var packNameOf = function packNameOf(input) {
  if (input.kind === 'file') return input.file.name;
  try {
    var path = new URL(input.url, 'http://localhost').pathname;
    return decodeURIComponent(path.split('/').pop() || 'pack.evidence');
  } catch (_unused) {
    return 'pack.evidence';
  }
};

/** Entries whose bytes we never LRU-cache — they are parsed then dropped. */
var isYamlEntry = function isYamlEntry(rel) {
  return rel === 'run.yaml' || /^tests\/[^/]+\/result\.yaml$/.test(rel);
};

/**
 * The v1, in-thread PackClient: ByteSource → ranged zip container → parsed
 * view models. A future WorkerPackClient implements the same interface over
 * postMessage; nothing above this layer changes.
 */
export function createLocalPackClient() {
  var container = null;
  var metered = null;
  var artifactCache = new ByteBudgetLru(ARTIFACT_CACHE_BYTES);
  var statsListeners = new Set();
  var requireContainer = function requireContainer() {
    if (!container) {
      throw new PackError('not-a-pack', 'no pack is open');
    }
    return container;
  };
  return {
    open: function open(input, opts) {
      return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var raw, manifestText, header, testIds;
        return _regeneratorRuntime.wrap(function (_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              raw = input.kind === 'file' ? new BlobByteSource(input.file) : new HttpByteSource(input.url);
              metered = new MeteredByteSource(raw, function (s) {
                var _iterator = _createForOfIteratorHelper(statsListeners),
                  _step;
                try {
                  for (_iterator.s(); !(_step = _iterator.n()).done;) {
                    var cb = _step.value;
                    cb(s);
                  }
                } catch (err) {
                  _iterator.e(err);
                } finally {
                  _iterator.f();
                }
              });
              _context.next = 1;
              return openPackZip(metered, packNameOf(input), opts);
            case 1:
              container = _context.sent;
              _context.next = 2;
              return container.readManifest(opts);
            case 2:
              manifestText = _context.sent;
              if (!(manifestText === null)) {
                _context.next = 3;
                break;
              }
              throw new PackError('not-a-pack', 'this archive has no top-level run.yaml — not an evidence pack');
            case 3:
              header = toRunVM(parseRunManifest(manifestText));
              _context.next = 4;
              return container.listTestIds();
            case 4:
              testIds = _context.sent;
              return _context.abrupt("return", {
                header: header,
                testIds: testIds
              });
            case 5:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }))();
    },
    readTestRow: function readTestRow(id, signal) {
      return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        var pack, text;
        return _regeneratorRuntime.wrap(function (_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              pack = requireContainer();
              _context2.next = 1;
              return pack.readResult(id, {
                signal: signal
              });
            case 1:
              text = _context2.sent;
              if (!(text === null)) {
                _context2.next = 2;
                break;
              }
              throw new PackError('not-a-pack', "tests/".concat(id, "/result.yaml is missing"), {
                entry: "tests/".concat(id, "/result.yaml")
              });
            case 2:
              return _context2.abrupt("return", toTestRow(id, parseTestResult(text)));
            case 3:
            case "end":
              return _context2.stop();
          }
        }, _callee2);
      }))();
    },
    readCoverage: function readCoverage() {
      return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        var text, _t;
        return _regeneratorRuntime.wrap(function (_context3) {
          while (1) switch (_context3.prev = _context3.next) {
            case 0:
              _context3.prev = 0;
              _context3.next = 1;
              return requireContainer().readText('coverage/usecases.yaml');
            case 1:
              text = _context3.sent;
              return _context3.abrupt("return", text === null ? null : parseCoverage(text));
            case 2:
              _context3.prev = 2;
              _t = _context3["catch"](0);
              return _context3.abrupt("return", null);
            case 3:
            case "end":
              return _context3.stop();
          }
        }, _callee3, null, [[0, 2]]);
      }))();
    },
    readAttention: function readAttention(onIndex) {
      return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        var _t2;
        return _regeneratorRuntime.wrap(function (_context4) {
          while (1) switch (_context4.prev = _context4.next) {
            case 0:
              _context4.prev = 0;
              _context4.next = 1;
              return _readAttention(requireContainer(), MAX_ATTENTION_ITEMS, onIndex);
            case 1:
              return _context4.abrupt("return", _context4.sent);
            case 2:
              _context4.prev = 2;
              _t2 = _context4["catch"](0);
              return _context4.abrupt("return", null);
            case 3:
            case "end":
              return _context4.stop();
          }
        }, _callee4, null, [[0, 2]]);
      }))();
    },
    readTestDetail: function readTestDetail(testId, signal) {
      return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        var _ref4, _ref5, _ref6, _ref7, _ref8, _ref9;
        var pack, readTextCapped, readFailure, findVideo, _yield$Promise$allSet, _yield$Promise$allSet2, result, execution, steps, logs, failure, videoPath;
        return _regeneratorRuntime.wrap(function (_context8) {
          while (1) switch (_context8.prev = _context8.next) {
            case 0:
              pack = requireContainer(); // Size-guarded small-text read; content problems resolve to null.
              readTextCapped = /*#__PURE__*/function () {
                var _ref = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee5(rel, maxBytes) {
                  var size;
                  return _regeneratorRuntime.wrap(function (_context5) {
                    while (1) switch (_context5.prev = _context5.next) {
                      case 0:
                        size = pack.entrySize(rel);
                        if (!(size !== null && size > maxBytes)) {
                          _context5.next = 1;
                          break;
                        }
                        return _context5.abrupt("return", null);
                      case 1:
                        return _context5.abrupt("return", pack.readText(rel, {
                          signal: signal
                        }));
                      case 2:
                      case "end":
                        return _context5.stop();
                    }
                  }, _callee5);
                }));
                return function readTextCapped(_x, _x2) {
                  return _ref.apply(this, arguments);
                };
              }();
              readFailure = /*#__PURE__*/function () {
                var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
                  var _index$failures, _detail$step;
                  var indexText, index, ref, detail, text;
                  return _regeneratorRuntime.wrap(function (_context6) {
                    while (1) switch (_context6.prev = _context6.next) {
                      case 0:
                        _context6.next = 1;
                        return pack.readText('failure.yaml', {
                          signal: signal
                        });
                      case 1:
                        indexText = _context6.sent;
                        if (!(indexText === null)) {
                          _context6.next = 2;
                          break;
                        }
                        return _context6.abrupt("return", null);
                      case 2:
                        index = parseFailureIndex(indexText);
                        ref = index === null || index === void 0 || (_index$failures = index.failures) === null || _index$failures === void 0 ? void 0 : _index$failures.find(function (f) {
                          return f.test === testId;
                        });
                        if (ref) {
                          _context6.next = 3;
                          break;
                        }
                        return _context6.abrupt("return", null);
                      case 3:
                        detail = {};
                        if (!(typeof ref.path === 'string' && ref.path !== '')) {
                          _context6.next = 5;
                          break;
                        }
                        _context6.next = 4;
                        return pack.readText(ref.path, {
                          signal: signal
                        });
                      case 4:
                        text = _context6.sent;
                        if (text !== null) detail = parseFailureDetail(text);
                      case 5:
                        return _context6.abrupt("return", {
                          message: detail.message,
                          rootCause: detail.rootCause,
                          suggestedFix: detail.suggestedFix,
                          anchorStepId: (_detail$step = detail.step) !== null && _detail$step !== void 0 ? _detail$step : typeof ref.step === 'string' ? ref.step : undefined
                        });
                      case 6:
                      case "end":
                        return _context6.stop();
                    }
                  }, _callee6);
                }));
                return function readFailure() {
                  return _ref2.apply(this, arguments);
                };
              }(); // A pack-internal video is central-directory knowledge — free checks.
              findVideo = /*#__PURE__*/function () {
                var _ref3 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
                  var _i, _arr, ext, rel;
                  return _regeneratorRuntime.wrap(function (_context7) {
                    while (1) switch (_context7.prev = _context7.next) {
                      case 0:
                        _i = 0, _arr = ['mp4', 'webm'];
                      case 1:
                        if (!(_i < _arr.length)) {
                          _context7.next = 4;
                          break;
                        }
                        ext = _arr[_i];
                        rel = "tests/".concat(testId, "/video.").concat(ext);
                        _context7.next = 2;
                        return pack.exists(rel);
                      case 2:
                        if (!_context7.sent) {
                          _context7.next = 3;
                          break;
                        }
                        return _context7.abrupt("return", rel);
                      case 3:
                        _i++;
                        _context7.next = 1;
                        break;
                      case 4:
                        return _context7.abrupt("return", null);
                      case 5:
                      case "end":
                        return _context7.stop();
                    }
                  }, _callee7);
                }));
                return function findVideo() {
                  return _ref3.apply(this, arguments);
                };
              }(); // Six concurrent reads; each section degrades to null independently —
              // a broken execution.json must not cost the steps/ folder scan.
              _context8.next = 1;
              return Promise.allSettled([pack.readResult(testId, {
                signal: signal
              }).then(function (text) {
                return text === null ? null : parseTestResult(text);
              }), readTextCapped("tests/".concat(testId, "/auteur/execution.json"), MAX_EXECUTION_JSON_BYTES).then(function (text) {
                return text === null ? null : parseExecution(text);
              }), listSteps(pack, testId), listLogs(pack, testId), readFailure(), findVideo()]).then(function (settled) {
                var aborted = settled.find(function (s) {
                  return s.status === 'rejected' && isAbort(s.reason);
                });
                if (aborted && aborted.status === 'rejected') throw aborted.reason;
                return settled.map(function (s) {
                  return s.status === 'fulfilled' ? s.value : null;
                });
              });
            case 1:
              _yield$Promise$allSet = _context8.sent;
              _yield$Promise$allSet2 = _slicedToArray(_yield$Promise$allSet, 6);
              result = _yield$Promise$allSet2[0];
              execution = _yield$Promise$allSet2[1];
              steps = _yield$Promise$allSet2[2];
              logs = _yield$Promise$allSet2[3];
              failure = _yield$Promise$allSet2[4];
              videoPath = _yield$Promise$allSet2[5];
              return _context8.abrupt("return", {
                result: (_ref4 = result) !== null && _ref4 !== void 0 ? _ref4 : null,
                execution: (_ref5 = execution) !== null && _ref5 !== void 0 ? _ref5 : null,
                steps: (_ref6 = steps) !== null && _ref6 !== void 0 ? _ref6 : [],
                logs: (_ref7 = logs) !== null && _ref7 !== void 0 ? _ref7 : [],
                failure: (_ref8 = failure) !== null && _ref8 !== void 0 ? _ref8 : null,
                videoPath: (_ref9 = videoPath) !== null && _ref9 !== void 0 ? _ref9 : null
              });
            case 2:
            case "end":
              return _context8.stop();
          }
        }, _callee8);
      }))();
    },
    readLogLines: function readLogLines(rel, signal) {
      return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee9() {
        var pack, size, text;
        return _regeneratorRuntime.wrap(function (_context9) {
          while (1) switch (_context9.prev = _context9.next) {
            case 0:
              pack = requireContainer();
              size = pack.entrySize(rel);
              if (!(size === null)) {
                _context9.next = 1;
                break;
              }
              return _context9.abrupt("return", null);
            case 1:
              if (!(size > MAX_LOG_TEXT_BYTES)) {
                _context9.next = 2;
                break;
              }
              return _context9.abrupt("return", {
                kind: 'too-large',
                totalBytes: size
              });
            case 2:
              _context9.next = 3;
              return pack.readText(rel, {
                signal: signal
              });
            case 3:
              text = _context9.sent;
              if (!(text === null)) {
                _context9.next = 4;
                break;
              }
              return _context9.abrupt("return", null);
            case 4:
              return _context9.abrupt("return", {
                kind: 'ready',
                lines: splitNdjsonLines(text),
                totalBytes: size
              });
            case 5:
            case "end":
              return _context9.stop();
          }
        }, _callee9);
      }))();
    },
    readHar: function readHar(rel, signal) {
      return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee0() {
        var pack, size, text, har;
        return _regeneratorRuntime.wrap(function (_context0) {
          while (1) switch (_context0.prev = _context0.next) {
            case 0:
              pack = requireContainer();
              size = pack.entrySize(rel);
              if (!(size === null)) {
                _context0.next = 1;
                break;
              }
              return _context0.abrupt("return", null);
            case 1:
              if (!(size > MAX_LOG_TEXT_BYTES)) {
                _context0.next = 2;
                break;
              }
              return _context0.abrupt("return", {
                kind: 'too-large',
                totalBytes: size
              });
            case 2:
              _context0.next = 3;
              return pack.readText(rel, {
                signal: signal
              });
            case 3:
              text = _context0.sent;
              if (!(text === null)) {
                _context0.next = 4;
                break;
              }
              return _context0.abrupt("return", null);
            case 4:
              har = parseHar(text);
              if (!(har === null)) {
                _context0.next = 5;
                break;
              }
              return _context0.abrupt("return", {
                kind: 'invalid'
              });
            case 5:
              return _context0.abrupt("return", {
                kind: 'ready',
                har: har,
                totalBytes: size
              });
            case 6:
            case "end":
              return _context0.stop();
          }
        }, _callee0);
      }))();
    },
    readBytes: function readBytes(rel, signal) {
      return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee1() {
        var pack, cached, bytes;
        return _regeneratorRuntime.wrap(function (_context1) {
          while (1) switch (_context1.prev = _context1.next) {
            case 0:
              pack = requireContainer();
              if (isYamlEntry(rel)) {
                _context1.next = 1;
                break;
              }
              cached = artifactCache.get(rel);
              if (!cached) {
                _context1.next = 1;
                break;
              }
              return _context1.abrupt("return", cached);
            case 1:
              _context1.next = 2;
              return pack.readBytes(rel, {
                signal: signal
              });
            case 2:
              bytes = _context1.sent;
              if (bytes && !isYamlEntry(rel)) artifactCache.set(rel, bytes);
              return _context1.abrupt("return", bytes);
            case 3:
            case "end":
              return _context1.stop();
          }
        }, _callee1);
      }))();
    },
    entrySize: function entrySize(rel) {
      return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
        return _regeneratorRuntime.wrap(function (_context10) {
          while (1) switch (_context10.prev = _context10.next) {
            case 0:
              return _context10.abrupt("return", requireContainer().entrySize(rel));
            case 1:
            case "end":
              return _context10.stop();
          }
        }, _callee10);
      }))();
    },
    listDir: function listDir(rel) {
      return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee11() {
        return _regeneratorRuntime.wrap(function (_context11) {
          while (1) switch (_context11.prev = _context11.next) {
            case 0:
              return _context11.abrupt("return", requireContainer().listDir(rel));
            case 1:
            case "end":
              return _context11.stop();
          }
        }, _callee11);
      }))();
    },
    listEntries: function listEntries() {
      return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee12() {
        return _regeneratorRuntime.wrap(function (_context12) {
          while (1) switch (_context12.prev = _context12.next) {
            case 0:
              return _context12.abrupt("return", requireContainer().listEntries());
            case 1:
            case "end":
              return _context12.stop();
          }
        }, _callee12);
      }))();
    },
    stats: function stats() {
      return metered ? metered.stats : null;
    },
    onStats: function onStats(cb) {
      statsListeners.add(cb);
      return function () {
        return statsListeners["delete"](cb);
      };
    },
    close: function close() {
      return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee13() {
        return _regeneratorRuntime.wrap(function (_context13) {
          while (1) switch (_context13.prev = _context13.next) {
            case 0:
              container = null;
              metered = null;
              artifactCache.clear();
              statsListeners.clear();
            case 1:
            case "end":
              return _context13.stop();
          }
        }, _callee13);
      }))();
    }
  };
}